/**
 * cart-modal-focus.js
 *
 * Focus trap for the mini-cart drawer (#t4s-mini_cart), which carries
 * role="dialog" aria-modal="true" but never enforced it -- Tab walked
 * straight into the header links behind the open panel.
 *
 * Same approach as search-modal-focus.js / login-modal-focus.js. Focusables
 * are re-queried per Tab, since the AJAX cart re-renders the drawer's
 * contents on every add/remove.
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // visibility:hidden keeps an element's box, so geometry alone calls it
  // visible while the browser still refuses focus() -- check styles too.
  function isVisible(el) {
    if (!el) return false;
    if (!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none') return false;
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    return true;
  }

  function getFocusableElements(container) {
    var nodes = container.querySelectorAll(FOCUSABLE_SELECTOR);
    var focusable = [];
    for (var i = 0; i < nodes.length; i++) {
      if (isVisible(nodes[i])) focusable.push(nodes[i]);
    }
    return focusable;
  }

  function createFocusTrap(isOpen) {
    var activeDrawer = null;
    var triggerElement = null;
    var keydownHandler = null;

    function trapTabKey(event) {
      if (event.key !== 'Tab' || !activeDrawer) return;

      // Fail open: only block Tab once focus is genuinely inside, or a
      // refused focus() leaves the user with a dead Tab key.
      var focusable = getFocusableElements(activeDrawer);
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (!activeDrawer.contains(document.activeElement)) {
        first.focus();
        if (activeDrawer.contains(document.activeElement)) event.preventDefault();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function open(drawer) {
      if (activeDrawer === drawer) return;
      activeDrawer = drawer;
      triggerElement = document.activeElement;

      if (!drawer.hasAttribute('tabindex')) {
        drawer.setAttribute('tabindex', '-1');
      }

      // The drawer's open transition (visibility/transform) can still be
      // mid-flight for a bit after aria-hidden flips, and a browser won't
      // move focus onto an element that isn't actually visible/painted
      // yet. Rather than guess a fixed delay, retry the focus() call
      // itself until it actually takes (or give up after ~3s).
      var attemptsLeft = 60;
      (function attemptFocus() {
        if (activeDrawer !== drawer) return;
        var focusable = getFocusableElements(drawer);
        var target = focusable[0] || drawer;
        target.focus();
        if (document.activeElement === target || attemptsLeft <= 0) return;
        attemptsLeft--;
        window.setTimeout(attemptFocus, 50);
      })();

      keydownHandler = trapTabKey;
      document.addEventListener('keydown', keydownHandler, true);
    }

    function close() {
      if (!activeDrawer) return;

      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler, true);
        keydownHandler = null;
      }

      if (triggerElement && typeof triggerElement.focus === 'function' && isVisible(triggerElement)) {
        triggerElement.focus();
      }

      activeDrawer = null;
      triggerElement = null;
    }

    return {
      sync: function (drawer) {
        if (isOpen(drawer)) {
          open(drawer);
        } else if (activeDrawer === drawer) {
          close();
        }
      }
    };
  }

  function init() {
    // sections/mini_cart.liquid and snippets/render_bottom.liquid BOTH render
    // a #t4s-mini_cart, so the id is duplicated in the document and only one
    // of them is the drawer that actually opens. getElementById() returns just
    // the first, which may be the inert one -- querySelectorAll() returns every
    // match, so each gets its own trap and whichever opens is handled.
    var drawers = document.querySelectorAll('#t4s-mini_cart');
    if (!drawers.length) return;

    for (var i = 0; i < drawers.length; i++) {
      (function (drawer) {
        var trap = createFocusTrap(function (el) {
          return el.getAttribute('aria-hidden') === 'false';
        });

        var observer = new MutationObserver(function () {
          trap.sync(drawer);
        });

        observer.observe(drawer, {
          attributes: true,
          attributeFilter: ['aria-hidden']
        });

        // In case the drawer is already open when this script runs.
        trap.sync(drawer);
      })(drawers[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
